// Acceso a la colección "inscripciones" en Firestore.
import { db } from "./firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  getDoc,
  deleteDoc,
  type Timestamp,
} from "firebase/firestore";

export type EstadoInscripcion = "pendiente" | "aprobado" | "negado";

// Datos que llena el corredor en el formulario.
export interface DatosInscripcion {
  nombres: string;
  cedula: string;
  fechaNacimiento: string; // "YYYY-MM-DD"
  distancia: string;
  categoria: string;
  sexo: "Damas" | "Varones";
  telefono: string;
  email: string;
  clubProvincia: string;
  tallaCamiseta: string;
  comprobanteUrl: string; // URL de la foto del comprobante (en Cloudinary)
  comentario: string; // duda / sugerencia opcional
}

// Documento tal como vive en Firestore (incluye metadatos).
export interface Inscripcion extends DatosInscripcion {
  id: string;
  estado: EstadoInscripcion;
  creadoEn?: Timestamp;
}

const COL = "inscripciones";
const COL_VERIF = "verificaciones";

// Datos públicos para la consulta por cédula (sin info sensible).
export interface Verificacion {
  nombres: string;
  cedula: string;
  distancia: string;
  categoria: string;
  estado: EstadoInscripcion;
}

function refVerif(cedula: string) {
  return doc(db, COL_VERIF, cedula.trim());
}

// Crea una inscripción nueva (estado inicial "pendiente").
export async function crearInscripcion(datos: DatosInscripcion): Promise<void> {
  await addDoc(collection(db, COL), {
    ...datos,
    estado: "pendiente" as EstadoInscripcion,
    creadoEn: serverTimestamp(),
  });

  // Registro público mínimo para la verificación por cédula.
  // Se envuelve en try/catch para no bloquear la inscripción si falla
  // (p. ej. una cédula ya registrada).
  try {
    await setDoc(refVerif(datos.cedula), {
      nombres: datos.nombres,
      cedula: datos.cedula.trim(),
      distancia: datos.distancia,
      categoria: datos.categoria,
      estado: "pendiente" as EstadoInscripcion,
    });
  } catch (e) {
    console.warn("No se pudo crear el registro de verificación:", e);
  }
}

// Consulta pública del estado por cédula.
export async function consultarVerificacion(cedula: string): Promise<Verificacion | null> {
  const snap = await getDoc(refVerif(cedula));
  return snap.exists() ? (snap.data() as Verificacion) : null;
}

// Escucha en tiempo real todas las inscripciones (para el panel admin).
// Devuelve la función para cancelar la suscripción.
export function suscribirInscripciones(
  callback: (lista: Inscripcion[]) => void,
  onError?: (e: unknown) => void
): () => void {
  const q = query(collection(db, COL), orderBy("creadoEn", "desc"));
  return onSnapshot(
    q,
    (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Inscripcion[];
      callback(lista);
    },
    (err) => onError?.(err)
  );
}

// Cambia el estado de una inscripción (aprobar / negar) y sincroniza
// el registro público de verificación. La sincronización es "best-effort":
// si falla (p. ej. registro antiguo sin verificación), no rompe la aprobación.
export async function cambiarEstadoInscripcion(
  id: string,
  estado: EstadoInscripcion,
  cedula?: string
): Promise<void> {
  await updateDoc(doc(db, COL, id), { estado });
  if (cedula) {
    try {
      await setDoc(refVerif(cedula), { estado }, { merge: true });
    } catch (e) {
      console.warn("No se pudo sincronizar la verificación:", e);
    }
  }
}

// Elimina una inscripción (y su registro de verificación). Solo admin.
export async function eliminarInscripcion(id: string, cedula?: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
  if (cedula) {
    try {
      await deleteDoc(refVerif(cedula));
    } catch (e) {
      console.warn("No se pudo eliminar la verificación:", e);
    }
  }
}

// ---------- Configuración: inscripciones abiertas / cerradas ----------
// Documento público que indica si las inscripciones están abiertas.
function refConfig() {
  return doc(db, "config", "inscripciones");
}

// Escucha en tiempo real si las inscripciones están abiertas.
// Por defecto abiertas (true) si el documento aún no existe.
export function suscribirInscripcionesAbiertas(
  callback: (abiertas: boolean) => void
): () => void {
  return onSnapshot(
    refConfig(),
    (snap) => callback(snap.data()?.abiertas !== false),
    () => callback(true)
  );
}

// Abre o cierra las inscripciones (solo admin).
export async function setInscripcionesAbiertas(abiertas: boolean): Promise<void> {
  await setDoc(refConfig(), { abiertas }, { merge: true });
}
